import React from 'react';
import { Box, Typography, Tooltip, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UploadedImageType } from './ImageUploadModal.types';
import { ReactComponent as EyeIcon } from '../../../assets/SVG/eye.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/SVG/DownloadDocs.svg';

// Custom File Type Icons
import { ReactComponent as PdfFileIcon } from '../../../assets/SVG/PDF.svg';
import { ReactComponent as DocxFileIcon } from '../../../assets/SVG/DOCX.svg';
import { ReactComponent as JpgFileIcon } from '../../../assets/SVG/JPG.svg';
import { ReactComponent as PngFileIcon } from '../../../assets/SVG/PNG.svg';
// Fallback if needed, though we cover main types
import { ReactComponent as DefaultFileIcon } from '../../../assets/SVG/pdfIcon.svg'; 

/**
 * Theme colors
 */
const THEME_COLORS = {
  primary: '#1D1D1D',
  secondary: '#8F9BB3', // Lighter text for size
  border: '#E4E9F2',
  background: '#FFFFFF',
  docBlue: '#1976D2',
  iconBg: '#F7F9FC',
};

/**
 * Helper to extract clean filename from S3 URL or path
 * Handles URLs with query parameters like ?X-Amz-Algorithm=...
 */
export const extractFilename = (urlOrName: string): string => {
  if (!urlOrName) return 'Unknown File';
  
  // If it's already a simple filename (not a URL)
  if (!urlOrName.includes('/') && !urlOrName.includes('?')) {
    return urlOrName;
  }
  
  try {
    // Remove query parameters first
    const urlWithoutQuery = urlOrName.split('?')[0];
    
    // Get the last part of the path
    const pathParts = urlWithoutQuery.split('/');
    const filename = pathParts[pathParts.length - 1];
    
    // Decode URL encoding (e.g., %20 -> space)
    return decodeURIComponent(filename) || 'Unknown File';
  } catch {
    // Fallback if decoding fails
    const parts = urlOrName.split('/');
    return parts[parts.length - 1].split('?')[0] || 'Unknown File';
  }
};

/**
 * Helper to determine file type for icon selection
 */
const getFileType = (file: UploadedImageType) => {
  const filename = extractFilename(file.name || file.image).toLowerCase();
  
  if (filename.endsWith('.pdf')) return 'PDF';
  if (filename.endsWith('.doc') || filename.endsWith('.docx')) return 'DOCX';
  if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) return 'JPG';
  if (filename.endsWith('.png')) return 'PNG';
  
  // Check MIME type if filename check fails
  if (file.image.startsWith('data:application/pdf')) return 'PDF';
  if (file.image.startsWith('data:image/jpeg')) return 'JPG';
  if (file.image.startsWith('data:image/png')) return 'PNG';

  return 'OTHER';
};


/**
 * File Icon Component
 */
const FileIcon: React.FC<{ type: string; size?: number }> = ({ type, size = 40 }) => {
  const props = { width: size, height: size };
  
  switch (type) {
  case 'PDF':
    return <PdfFileIcon {...props} />;
  case 'DOCX':
    return <DocxFileIcon {...props} />;
  case 'JPG':
    return <JpgFileIcon {...props} />;
  case 'PNG':
    return <PngFileIcon {...props} />;
  default:
    return <DefaultFileIcon {...props} />;
  }
};

/**
 * Helper to format file size from bytes to human-readable format
 */
const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  // Show 2 decimal places for MB and above, 0 for KB and B
  const decimals = unitIndex >= 2 ? 2 : (unitIndex === 1 ? 1 : 0);
  return `${size.toFixed(decimals)} ${units[unitIndex]}`;
};

export interface DocumentChipsProps {
  /** Array of uploaded files */
  files: UploadedImageType[];
  /** Callback when view is clicked on a file */
  onViewClick?: (index: number) => void;
}

/**
 * DocumentChips Component
 * 
 * Displays documents as card-styled chips
 */
const DocumentChips: React.FC<DocumentChipsProps> = ({
  files,
  onViewClick,
}) => {
  const { t: tGeneral } = useTranslation('general');
  
  if (!files || files.length === 0) {
    return null;
  }

  /**
   * Handle file download - fetches the file and triggers browser download
   * This will prompt the user to save the file (depending on browser settings)
   */
  const handleDownload = async (file: UploadedImageType) => {
    try {
      const filename = extractFilename(file.name || file.image);
      
      // For base64 data URLs, convert directly
      if (file.image.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = file.image;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      // For remote URLs (S3, etc.), fetch the file as blob to force download
      const response = await fetch(file.image);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab if download fails
      window.open(file.image, '_blank');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pb: 1 }}>
      {files.map((file, index) => {
        const filename = extractFilename(file.name || file.image);
        const fileType = getFileType(file);
        const fileSize = formatFileSize(file.sizeBytes);

        return (
          <Paper
            key={index}
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '280px', // Fixed width for card-like appearance
              p: 1.5,
              borderRadius: '12px',
              border: `1px solid ${THEME_COLORS.border}`,
              backgroundColor: THEME_COLORS.background,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                borderColor: '#C5CEE0',
              },
            }}
          >
            {/* File type icon */}
            <FileIcon type={fileType} size={40} />
            
            {/* File info */}
            <Box sx={{ minWidth: 0, ml: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                component="div"
                sx={{
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#232323',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  m: 0,
                  p: 0,
                }}
                title={filename}
              >
                {filename}
              </Typography>
              {fileSize && (
                <Typography
                  component="div"
                  sx={{
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: THEME_COLORS.secondary,
                    lineHeight: 1.2,
                    mt: 0.25,
                    m: 0,
                    p: 0,
                  }}
                >
                  {fileSize}
                </Typography>
              )}
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={tGeneral('view')}>
                <Box
                  onClick={() => onViewClick?.(index)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: THEME_COLORS.iconBg }
                  }}
                >
                  <EyeIcon width={20} height={20} />
                </Box>
              </Tooltip>
              <Tooltip title={tGeneral('download')}>
                <Box
                  onClick={() => handleDownload(file)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: '50%',
                    '&:hover': { backgroundColor: THEME_COLORS.iconBg }
                  }}
                >
                  <DownloadIcon width={20} height={20} />
                </Box>
              </Tooltip>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default DocumentChips;
